import { useState, useEffect, useMemo } from "react";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import { useLazyGetPageBySlugQuery } from "context/pages/pageApiSlice"; 
import DOMPurify from 'isomorphic-dompurify';
import parse from 'html-react-parser';

import Features from "components/Home/components/features";
import About from "components/Home/components/about";
import Services from "components/Home/components/services";
import Gallery from "components/Home/components/gallery";
import Testimonials from "components/Home/components/testimonials";
import Team from "components/Home/components/team";
import Clients from "components/Home/components/clients";
import Contact from "components/Home/components/contact";
import ReadMore from "components/Home/components/readMore";
import NotFound from "components/Home/Pages/notFound/components/NotFound";

// Authentication layout components
import CoverLayout from "components/layouts/CoverLayout";
import Breadcrumbs from "dashboard/examples/Breadcrumbs";

// @mui material components
import Card from "@mui/material/Card";
// Material Dashboard 2 React components
import MDBox from "dashboard/components/MDBox";

// Images
import defaultbgImage from "assets/images/covers/about.jpg";
import { useParams, useLocation } from "react-router-dom";
import SplashScreen  from "components/splash-screens/ScreenLoader";

import contactUS from "assets/images/covers/contact.jpg";
import productsImg from "assets/images/covers/screens.jpg";
import servicesImg from "assets/images/covers/it.png";
import aboutUS from "assets/images/covers/about.jpg";

const headerImages = {"About":aboutUS, "Contact":contactUS, "Services":servicesImg, "Features":productsImg};
const navbarRow = ({ breakpoints }, { isMini }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",

  [breakpoints.up("md")]: {
    justifyContent: isMini ? "space-between" : "stretch",
    width: isMini ? "100%" : "max-content",
  },

  [breakpoints.up("xl")]: {
    justifyContent: "stretch !important",
    width: "max-content !important",
  },
});

const parseContent = content => parse(DOMPurify?.sanitize(content));

function BasicPages({ absolute, light, isMini }) {
	const { slug, id } = useParams();

  const [controller] = useMaterialUIController();
  const { pageContent : homePage } = controller;
	const [pageContent,setPageContent] = useState({});
	const route = useLocation().pathname.split("/").slice(1);
  const [bgImage, setBgImage] = useState(defaultbgImage);
  const pageSection = useMemo(()=>  homePage && slug ? Object.entries(homePage).find(( [section, sectionContent]) => sectionContent.title?.toLowerCase() === decodeURI(slug).toLowerCase() ) : null,[homePage, slug]);
  
  if(id) {
    route.push(id); 
  }else if(slug){
    route.indexOf(slug) === -1 && route.push(slug);
  }
	const [getPage, { isLoading ,
    isSuccess,
		}] = useLazyGetPageBySlugQuery();

  const loadPageSlug = async ()=>{
    try {
      const responseData = await getPage({slug: decodeURI(slug)}).unwrap();
      const content = JSON.parse(responseData?.page.Content);
      return setPageContent(content);
    }catch (e){}
  }

  useEffect(() => {
    if(pageSection){
      const loadedPage = pageSection[1].cards  ? (id ? pageSection[1].cards.filter( item => item.title === id) : pageSection[1].cards) : pageSection;
      loadedPage && setPageContent(()=> pageSection[1].cards ? ({More : loadedPage}) : ({[loadedPage[0]]:loadedPage[1]}) );
      setBgImage(headerImages[pageSection[0]]);
    }else{
      loadPageSlug();
    }
  }, [pageSection,id]);

  return (
    <CoverLayout coverHeight="40vh" rest   image={bgImage}>
      <Card
          sx={{
            px: { xs: 1, lg: 3 },
            mx: { xs: 1, lg: 3 },
            mt: -4,
            mb: 4,
            backgroundColor: ({ palette: { background }, functions: { rgba } }) => rgba(background.card, 0.8),
            backdropFilter: "saturate(200%) blur(30px)",
            boxShadow: ({ boxShadows: { xxl } }) => xxl,
          }}
        >
				<MDBox color="inherit" m={{ xs: 1, md: 2 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route}  /> {/*light*/}
        </MDBox>
        {isLoading ? (<SplashScreen/>) : (
          <MDBox py={2}  px={{xs:.5, md:1,lg:3}}>
            {pageContent && Object.keys(pageContent).length !== 0 ?  (<MDBox>
            {pageContent.About && <About data={pageContent.About} parseContent={parseContent} />}
            {pageContent.Features && <Features data={pageContent.Features} parseContent={parseContent} />}
            {pageContent.Services && <Services data={pageContent.Services} parseContent={parseContent} />}
            {pageContent.Gallery && <Gallery data={pageContent.Gallery} parseContent={parseContent} />}
            {pageContent.Testimonials && <Testimonials data={pageContent.Testimonials} parseContent={parseContent} />}
            {pageContent.Team && <Team data={pageContent.Team} parseContent={parseContent} />}
            {pageContent.Clients && <Clients data={pageContent.Clients} parseContent={parseContent}/>}
            {pageContent.Contact && <Contact data={pageContent.Contact} parseContent={parseContent}/>}
            {pageContent.More && <ReadMore homePage={homePage}  section={pageSection[0]} data={pageContent.More} parseContent={parseContent} slug={slug}/>}
            </MDBox>
            )
            : <NotFound/>}
        </MDBox>)}
      </Card>
    </CoverLayout>
  );
}

// Setting default values for the props of DashboardNavbar
BasicPages.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
BasicPages.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default BasicPages;
